/**
 * 走っているジョブを取り消す・失敗したぶんをやり直す（FR26 / FR26.5）。
 *
 * この面が守っていること:
 * - **「止めました」と一律に書かない。** どこまで止まったかはサーバーが
 *   `cancelEffect` で返すので、画面はそれをそのまま出す。実行中の段が
 *   まだ走っているときは、そう書く（止まったふりをしない）。
 * - **押しても何も起きない導線を出さない。** やり直しのボタンは
 *   `Job.retryable` が真のときだけ出す。判断は画面に持たない。
 * - 取り消しは押す前に確かめる。実行中のものを止めると、途中まで作ったものが
 *   そこで止まるためである。
 * - 断られた理由（`userErrors`）はそのまま出す。
 */

import { useState } from "react";
import { useMutation } from "urql";

import { HelpIcon } from "../help/HelpIcon";
import {
  Button,
  CancelJobDocument,
  RetryJobDocument,
  Tag,
  type JobCancelEffect,
  type JobProgressFieldsFragment,
  type JobState,
} from "visumo-data-agent-web-shared";

/** 取り消しを押せる状態。終端のものは押させない */
const CANCELLABLE_STATES: readonly JobState[] = ["QUEUED", "RUNNING"];

/**
 * どこまで止まったかの言い方。
 *
 * サーバーの語彙（`JobCancelEffect`）と 1 対 1 で、**画面で言い換えない**。
 * 「止まりました」と言い切れるのは `NOT_STARTED` だけである。
 */
export const JOB_CANCEL_EFFECT_NOTE: Record<JobCancelEffect, string> = {
  NOT_STARTED: "待ち行列から取り消しました。この実行は動いていません。",
  STOPS_AT_NEXT_CHECKPOINT:
    "取り消しを受け付けました。いま処理している 1 件が終わったところで止まります。",
  RUNNING_STAGE_CONTINUES:
    "取り消しを受け付けました。実行は止めましたが、すでに始まっている文字起こしは最後まで走り、その本数ぶんの費用は出ます（結果は使いません）。",
};

/** 押す前の断り。**実行中は「途中まで」で終わる**ことを先に言う */
function confirmNoteFor(state: JobState): string {
  return state === "QUEUED"
    ? "この実行を取り消します。まだ始まっていないので、何も作られません。"
    : "この実行を取り消します。ここまでに作られたものは残り、残りは作られません。";
}

export function JobControls({
  job,
  onChanged,
}: {
  job: JobProgressFieldsFragment;
  /**
   * 取り消し・やり直しのあとに画面へ返す Job。
   * やり直しでは**別のジョブ**が返るので、呼び出し側が見る先を切り替える。
   */
  onChanged: (next: JobProgressFieldsFragment) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [errors, setErrors] = useState<readonly string[]>([]);
  const [cancelResult, cancelJob] = useMutation(CancelJobDocument);
  const [retryResult, retryJob] = useMutation(RetryJobDocument);

  const busy = cancelResult.fetching || retryResult.fetching;
  const cancellable = CANCELLABLE_STATES.includes(job.state);

  async function runCancel(): Promise<void> {
    setErrors([]);
    setNotice(null);
    const result = await cancelJob({ input: { jobId: job.id } });
    const payload = result.data?.cancelJob;
    if (result.error !== undefined) {
      setErrors([result.error.message]);
      return;
    }
    if (payload === undefined) return;
    if (payload.userErrors.length > 0) {
      setErrors(payload.userErrors.map((entry) => entry.message));
      setConfirming(false);
      /**
       * **断りと一緒に返ってきた台帳で、画面を読み直す。**
       *
       * 断る理由はたいてい「画面が持っている姿が古い」ことである
       * (終わったあとに取り消しを押した、など)。断りだけを出して画面を
       * そのままにすると、「実行中」と「もう終わっています」が同時に出て、
       * どちらが本当か読めない。2026-09-21 に dev の通しで実際にそうなった。
       */
      if (payload.job !== null && payload.job !== undefined) {
        onChanged(payload.job);
      }
      return;
    }
    setConfirming(false);
    if (payload.job !== null && payload.job !== undefined) {
      // どこまで止まったかは、返ってきた Job がそのまま持っている
      const effect = payload.job.cancelEffect;
      setNotice(effect === null ? null : JOB_CANCEL_EFFECT_NOTE[effect]);
      onChanged(payload.job);
    }
  }

  async function runRetry(): Promise<void> {
    setErrors([]);
    setNotice(null);
    const result = await retryJob({ input: { jobId: job.id } });
    const payload = result.data?.retryJob;
    if (result.error !== undefined) {
      setErrors([result.error.message]);
      return;
    }
    if (payload === undefined) return;
    if (payload.userErrors.length > 0) {
      setErrors(payload.userErrors.map((entry) => entry.message));
      // 断りと一緒に返ってきた台帳で読み直す(取り消しと同じ理由)。
      // **ただし別のジョブへ移らない** — 再実行の断りでは元のジョブが返る
      if (payload.job !== null && payload.job !== undefined) {
        onChanged(payload.job);
      }
      return;
    }
    if (payload.job !== null && payload.job !== undefined) {
      setNotice("失敗したぶんだけを、新しい実行として流し直しました。");
      // 見る先を新しいジョブへ移す(元のジョブはもう動かない)
      onChanged(payload.job);
    }
  }

  if (
    !cancellable &&
    !job.retryable &&
    notice === null &&
    errors.length === 0
  ) {
    return null;
  }

  return (
    <div className="vd-job-controls" style={{ marginTop: 12 }}>
      <HelpIcon slug="getting-started/jobs" />
      {errors.length === 0 ? null : (
        <ul className="vd-job-reasons" role="alert">
          {errors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}

      {notice === null ? null : (
        <p className="vd-stat-note" role="status" style={{ marginTop: 0 }}>
          {notice}
        </p>
      )}

      {confirming ? (
        <div role="group" aria-label="取り消しの確認">
          <p style={{ margin: "4px 0 8px" }}>{confirmNoteFor(job.state)}</p>
          <Button onClick={runCancel} disabled={busy}>
            取り消す
          </Button>
          <Button
            variant="text"
            onClick={() => setConfirming(false)}
            disabled={busy}
          >
            やめる
          </Button>
        </div>
      ) : (
        <>
          {cancellable ? (
            <Button variant="text" onClick={() => setConfirming(true)}>
              この実行を取り消す
            </Button>
          ) : null}
          {job.retryable ? (
            <Button onClick={runRetry} disabled={busy}>
              失敗したぶんをやり直す
            </Button>
          ) : null}
        </>
      )}

      {job.retriedByJobId === null ||
      job.retriedByJobId === undefined ? null : (
        <p className="vd-stat-note" style={{ marginBottom: 0 }}>
          この実行の失敗したぶんは、すでにやり直しています。
          <Tag>やり直し済み</Tag>
        </p>
      )}
    </div>
  );
}
